import { Project, ProjectLineItem } from "@interfaces/project";
import {
  decodeProjectSocialCommitment,
  encodeProjectSocialCommitment,
} from "./commitment";
import { PlaceLatLng } from "@interfaces/location";
import { getImageUrl } from "@hooks/utils/useUpload";

export const decodeProject = async (
  project: Project
): Promise<ProjectLineItem> => {
  const presignedUrl =
    project.organisation_logo.length > 0
      ? await getImageUrl(project.organisation_logo)
      : "";
  return {
    id: project.project_id,
    organisationId: project.organisation_id,
    organisationName: project.organisation_name,
    representativeEmail: project.representative_email,
    representativeName: project.representative_name,
    organisationLogo: presignedUrl,
    projectName: project.project_name,
    phoneNumber: project.phone_number,
    faxNumber: project.fax_number,
    projectAddressLine1: project.project_address_line1,
    projectAddressLine2: project.project_address_line2,
    projectCountry: project.project_country,
    projectState: project.project_state,
    projectCity: project.project_city,
    projectPostCode: project.project_post_code,
    geoLocation: {
      lat: project.geo_location_lat,
      lng: project.geo_location_long,
    } as PlaceLatLng,
    reportingDueDate: project.reporting_due_date,
    siteAddressSameAsProject: project.site_address_same_as_project,
    siteAddressLine1: project.site_address_line1,
    siteAddressLine2: project.site_address_line2,
    siteCountry: project.site_country,
    siteState: project.site_state,
    siteCity: project.site_city,
    sitePostCode: project.site_post_code,
    sitePhoneNumber: project.site_phone_number,
    projectOwner: project.project_owner,
    totalProjectValue: project.total_project_value,
    projectMargin: project.project_margin,
    specialisedEquipmentValue: project.specialised_equipment_value,
    deemedLabourRatio: project.deemed_labour_ratio,
    deemedLabourHourlyRate: project.deemed_labour_hourly_rate,
    requiredHours: project.required_hours,
    requiredSpend: project.required_spend,
    startDate: new Date(project.start_date),
    endDate: new Date(project.end_date),
    firstPackageStartDate: project.first_package_start_date,
    lastPackageEndDate: project.last_package_end_date,
    estimatedProgress: project.estimated_progress,
    created: new Date(project.created),
    archived: project.archived,
    commitments: project.commitments.map((c) =>
      decodeProjectSocialCommitment(c)
    ),
  };
};

export const encodeProject = (project: ProjectLineItem): Project => {
  return {
    project_id: project.id,
    organisation_id: project.organisationId,
    organisation_name: project.organisationName,
    organisation_logo: project.organisationLogo,
    representative_name: project.representativeName,
    representative_email: project.representativeEmail,
    project_name: project.projectName,
    phone_number: project.phoneNumber,
    fax_number: project.faxNumber,
    project_address_line1: project.projectAddressLine1,
    project_address_line2: project.projectAddressLine2,
    project_country: project.projectCountry,
    project_state: project.projectState,
    project_city: project.projectCity,
    project_post_code: project.projectPostCode,
    geo_location_lat: project.geoLocation.lat,
    geo_location_long: project.geoLocation.lng,
    reporting_due_date: project.reportingDueDate,
    site_address_same_as_project: project.siteAddressSameAsProject,
    site_address_line1: project.siteAddressLine1,
    site_address_line2: project.siteAddressLine2,
    site_country: project.siteCountry,
    site_state: project.siteState,
    site_city: project.siteCity,
    site_post_code: project.sitePostCode,
    site_phone_number: project.sitePhoneNumber,
    project_owner: project.projectOwner,
    total_project_value: project.totalProjectValue,
    project_margin: project.projectMargin,
    specialised_equipment_value: project.specialisedEquipmentValue,
    required_spend: project.requiredSpend,
    deemed_labour_ratio: project.deemedLabourRatio,
    deemed_labour_hourly_rate: project.deemedLabourHourlyRate,
    required_hours: project.requiredHours,
    start_date: project.startDate,
    end_date: project.endDate,
    first_package_start_date: project.firstPackageStartDate,
    last_package_end_date: project.lastPackageEndDate,
    estimated_progress: project.estimatedProgress,
    created: new Date(project.created),
    archived: project.archived,
    commitments: project.commitments.map((c) =>
      encodeProjectSocialCommitment(c)
    ),
  };
};
